import React, { useEffect, useId, useRef, useState, type ReactNode } from 'react'
import * as Sentry from '@sentry/nextjs'
import { Button, Modal, toastManager } from '@systemeio/components'
import { useTranslation } from 'next-i18next/pages'
import type { ReactCropperElement } from 'react-cropper'
import Cropper from 'react-cropper'
import type { FileUploadErrorResponseInterface } from 'shared/components/image-uploader/types'
import { BadRequest } from 'shared/errors/bad-request'
import { useUploadImage } from 'shared/hooks/use-upload-image'
import type { DataFile } from 'shared/types/datafile-type'

type ImageCropperType = Omit<React.ComponentPropsWithoutRef<typeof Cropper>, 'src'>

export type ImageCropperAndUploaderProps = {
  onUploadSuccess: (imageFileData: DataFile) => void
  imageFile: File | undefined
  source?: string
  type?: string
  isOpened: boolean
  setIsOpened: React.Dispatch<React.SetStateAction<boolean>>
  imageCropperProps?: ImageCropperType
  isRounded?: boolean
} & { onClose: () => void }

export const ImageCropperAndUploader = ({
  imageCropperProps: imageCropperProps,
  imageFile,
  type,
  source,
  isOpened,
  setIsOpened,
  onClose,
  onUploadSuccess,
  isRounded,
}: ImageCropperAndUploaderProps): ReactNode => {
  const {
    viewMode = 1,
    autoCropArea = 1,
    aspectRatio = 1,
    checkOrientation = false, // NOTE: it fixes this issue https://github.com/fengyuanchen/cropperjs/issues/671
    background = true,
    dragMode = 'move',
    toggleDragModeOnDblclick = false,
    style = { height: 400, width: '100%' },
    ...cropperPropsRest
  } = imageCropperProps || {}

  const { t } = useTranslation()
  const previewId = useId().replaceAll(':', '')

  const cropperRef = useRef<ReactCropperElement>(null)
  const cropperWrapperRef = useRef<HTMLDivElement>(null)
  const previewRef = useRef<HTMLDivElement>(null)
  const finalPreviewRef = useRef<HTMLDivElement>(null)

  const [uploadError, setUploadError] = useState<string | null>(null)
  const [imageSrc, setImageSrc] = useState<string>()

  const { uploadImage, isFetching } = useUploadImage()

  useEffect(() => {
    if (!imageFile) {
      setImageSrc(undefined)
      return
    }

    const nextImageSrc = URL.createObjectURL(imageFile)
    setImageSrc(nextImageSrc)

    return () => {
      URL.revokeObjectURL(nextImageSrc)
    }
  }, [imageFile])

  const hidePreview = (): void => {
    if (cropperWrapperRef.current) cropperWrapperRef.current.style.opacity = ''

    if (previewRef.current) previewRef.current.style.display = 'block'
    if (finalPreviewRef.current) {
      finalPreviewRef.current.style.zIndex = ''
      finalPreviewRef.current.replaceChildren()
    }
  }
  const showPreview = (): void => {
    //  NOTE: The Cropper resets the cropBox after render because of that we overlay it with the finalPreviewRef.
    // We can't use the previewRef because it's controlled by the Cropper, so it changes after render too.
    if (previewRef.current && finalPreviewRef.current) {
      finalPreviewRef.current.replaceChildren(previewRef.current.cloneNode(true))
      finalPreviewRef.current.style.zIndex = '1000'
      previewRef.current.style.display = 'none'
    }

    //  NOTE: we use the cropperWrapperRef because applying styles to the cropperRef is buggy
    if (cropperWrapperRef.current) cropperWrapperRef.current.style.opacity = '0'
  }

  const onLoad = async function (): Promise<void> {
    try {
      if (cropperRef.current?.cropper !== undefined) {
        showPreview()

        // Create a cropped file client-side and upload it
        const originalFile = imageFile!
        const canvas = cropperRef.current.cropper.getCroppedCanvas()

        if (!canvas) {
          Sentry.captureMessage('Failed to get cropped canvas', {
            level: 'error',
            extra: {
              source,
              type,
              fileName: imageFile?.name,
              fileSize: imageFile?.size,
            },
          })
        }

        const blob: Blob = await new Promise((resolve, reject) => {
          const outputType = originalFile?.type || 'image/png'
          canvas.toBlob(b => {
            b ? resolve(b) : reject(new Error(t('error.image_uploader.crop_failed')))
          }, outputType)
        })

        const croppedFile = new File([blob], originalFile?.name || 'image.png', {
          type: blob.type,
          lastModified: Date.now(),
        })

        const imageFileData = await uploadImage({
          file: croppedFile,
          source,
        })
        onUploadSuccess(imageFileData)
        setIsOpened(false)
      }
    } catch (error) {
      Sentry.captureMessage('Image upload failed', {
        level: 'error',
        extra: {
          error:
            error instanceof Error
              ? { name: error.name, message: error.message, stack: error.stack }
              : error,
          source,
          type,
          fileName: imageFile?.name,
          fileSize: imageFile?.size,
        },
      })

      if (error instanceof Error && error?.message && typeof error.message === 'string') {
        const msg = error.message.trim()
        setUploadError(msg)
      } else if (error instanceof BadRequest) {
        const { errors } = error as unknown as FileUploadErrorResponseInterface

        if (Array.isArray(errors)) {
          setUploadError(errors.join('\n'))
        } else if (errors !== null && typeof errors === 'object') {
          const typedErrors = errors as {
            common?: string[]
            image?: { file?: string[] }
            file?: string[]
          }
          const commonErrors = typedErrors.common
          const imageFileErrors = typedErrors.image?.file
          const fileErrors = typedErrors.file

          if (Array.isArray(commonErrors)) {
            setUploadError(commonErrors.join('\n'))
          } else if (Array.isArray(imageFileErrors)) {
            const msg = imageFileErrors.join('\n')
            setUploadError(msg)
          } else if (Array.isArray(fileErrors)) {
            const msg = fileErrors.join('\n')
            setUploadError(msg)
          }
        }
      }

      hidePreview()
      toastManager.add({ type: 'error', title: uploadError || t('global.error') })
    }
  }

  return (
    <Modal.Root
      open={isOpened}
      onOpenChange={open => {
        if (!open) setIsOpened(false)
      }}
      onOpenChangeComplete={open => {
        if (!open) {
          setUploadError(null)
          onClose()
          hidePreview()
        }
      }}
    >
      <Modal.Popup>
        <Modal.Header>
          <Modal.Title>{t('global.upload_image')}</Modal.Title>
        </Modal.Header>
        <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-8 py-6">
          <div className="relative w-full overflow-hidden">
            <div
              ref={finalPreviewRef}
              className="absolute inset-0 flex items-center justify-center"
            />
            <div
              id={previewId}
              data-is-rounded={isRounded}
              className="absolute left-1/2 h-[400px] w-full max-w-[400px] -translate-x-1/2 overflow-hidden data-[is-rounded=true]:rounded-[50%]"
              ref={previewRef}
            />
            <div ref={cropperWrapperRef} className="mx-auto w-full max-w-[400px]">
              <Cropper
                className={isRounded ? 'sio-cropper-rounded' : ''}
                ref={cropperRef}
                src={imageSrc}
                preview={`#${previewId}`}
                style={style}
                viewMode={viewMode}
                background={background}
                autoCropArea={autoCropArea}
                aspectRatio={aspectRatio}
                checkOrientation={checkOrientation}
                dragMode={dragMode}
                toggleDragModeOnDblclick={toggleDragModeOnDblclick}
                {...cropperPropsRest}
              />
            </div>
          </div>
          {uploadError ? (
            <p
              className="text-center text-base text-text-error"
              dangerouslySetInnerHTML={{ __html: uploadError }}
            />
          ) : null}
        </div>
        <Modal.Footer>
          <Button variant="primary" onClick={onLoad} isLoading={isFetching}>
            {t('global.click_to_upload')}
          </Button>
        </Modal.Footer>
      </Modal.Popup>
    </Modal.Root>
  )
}
